import React, { useEffect, useState, useRef } from 'react';
import { 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Box,
  Chip,
  Avatar,
  Button,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  CircularProgress
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import { useTranslation } from '../hooks/useTranslation';
import { useCheckout } from '../contexts/CheckoutContext';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { ProductType } from '../contexts/CheckoutContext';

const StyledTableContainer = styled(TableContainer)(() => ({
  borderRadius: 12,
  boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  overflow: 'hidden',
  background: '#ffffff',
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  backgroundColor: '#f8fafc',
  '& .MuiTableCell-head': {
    color: '#374151',
    fontWeight: 600,
    fontSize: '0.875rem',
    letterSpacing: '0.025em',
    textTransform: 'uppercase',
    borderBottom: '1px solid #e5e7eb',
    padding: theme.spacing(2, 3),
    position: 'relative',
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  background: '#ffffff',
  transition: 'background-color 0.2s ease',
  '&:nth-of-type(even)': {
    background: '#f9fafb',
  },
  '&:hover': {
    background: '#f3f4f6',
  },
  '&:last-child td': {
    borderBottom: 'none',
  },
  '& .MuiTableCell-root': {
    borderBottom: '1px solid #f3f4f6',
    padding: theme.spacing(2, 3),
    verticalAlign: 'middle',
  },
}));

const ProductImage = styled(Avatar)(() => ({
  width: 48,
  height: 48,
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  minWidth: 'auto',
  padding: theme.spacing(0.75, 1.5),
  borderRadius: 6,
  textTransform: 'none',
  fontSize: '0.8rem',
  fontWeight: 500,
  backgroundColor: '#3b82f6',
  color: '#ffffff',
  border: 'none',
  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: '#2563eb',
    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
  },
}));


const EmptyStateCard = styled(Card)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(4),
  borderRadius: 16,
  boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
}));

const MobileCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderRadius: 12,
  boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
  border: '1px solid rgba(0,0,0,0.06)',
  overflow: 'hidden',
}));

const getProductTypeLabel = (productType: ProductType, t: any) => {
  switch (productType) {
    case ProductType.TUTORIAL:
      return t('myOrders.productTypes.tutorial');
    case ProductType.WORKSHOP:
      return t('myOrders.productTypes.workshop');
    case ProductType.ITEM:
      return t('myOrders.productTypes.item');
    case ProductType.FILE:
      return t('myOrders.productTypes.file');
    default:
      return productType;
  }
};


export const MyOrdersPage: React.FC = () => {
  const { t } = useTranslation();
  const { purchaseHistory, isPurchaseHistoryLoading, refreshPurchaseHistory } = useCheckout();
  const { isAuthenticated } = useAuth();
  const { currentLang } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const hasFetchedData = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(false);
      // Only fetch data once when the page mounts
      if (!hasFetchedData.current) {
        hasFetchedData.current = true;
        setRefreshing(true);
        refreshPurchaseHistory()
          .catch((error) => {
            console.error('Failed to fetch purchase history:', error);
          })
          .finally(() => setRefreshing(false));
      }
    } else {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: location } });
    }
  }, [isAuthenticated, navigate, location, refreshPurchaseHistory]);


  if (loading || isPurchaseHistoryLoading || refreshing) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress size={60} sx={{ mb: 2 }} />
        <Typography variant="h5" color="text.secondary">
          {t('myOrders.loading')}
        </Typography>
      </Box>
    );
  }

  if (purchaseHistory.length === 0) {
    return (
      <Box sx={{ maxWidth: 600, mx: 'auto', py: 8 }}>
        <EmptyStateCard>
          <ShoppingBagIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 600, color: 'primary.main' }}>
            {t('myOrders.empty.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            {t('myOrders.empty.description')}
          </Typography>
        </EmptyStateCard>
      </Box>
    );
  }

  // Sort by order date (most recent first)
  const sortedHistory = [...purchaseHistory].sort((a, b) => 
    new Date(b.time).getTime() - new Date(a.time).getTime()
  );

  const handleAction = (_item: any, productType: ProductType, order?: any) => {
    if (productType === ProductType.FILE) {
      // Handle file download - open download path in new tab
      // The downloadPath should be in productDetails since order.item is just an ObjectId
      const downloadPath = order?.productDetails?.downloadPath;
      
      if (downloadPath) {
        window.open(downloadPath, '_blank');
      } else {
        console.error('No download path available for file');
        console.error('Order data:', order);
      }
    } else if (productType === ProductType.TUTORIAL) {
      // Handle tutorial open - navigate to tutorial viewer
      // Use the item ID from the order (which is the ObjectId)
      const tutorialId = order?.item;
      if (tutorialId) {
        navigate(`/tutorials/${tutorialId}`);
      } else {
        console.error('No tutorial ID available');
      }
    }
  };


  const renderDesktopTable = () => (
    <StyledTableContainer>
      <Table sx={{ minWidth: 800 }} aria-label="orders table">
        <StyledTableHead>
          <TableRow>
            {currentLang === 'he' ? (
              <>
                <TableCell align="center">{t('myOrders.table.action')}</TableCell>
                <TableCell align="center">{t('myOrders.table.image')}</TableCell>
                <TableCell align="right">{t('myOrders.table.amount')}</TableCell>
                <TableCell align="right">{t('myOrders.table.name')}</TableCell>
                <TableCell align="right">{t('myOrders.table.productType')}</TableCell>
                <TableCell align="right">{t('myOrders.table.orderDate')}</TableCell>
              </>
            ) : (
              <>
                <TableCell>{t('myOrders.table.orderDate')}</TableCell>
                <TableCell>{t('myOrders.table.productType')}</TableCell>
                <TableCell>{t('myOrders.table.name')}</TableCell>
                <TableCell>{t('myOrders.table.amount')}</TableCell>
                <TableCell>{t('myOrders.table.image')}</TableCell>
                <TableCell align="center">{t('myOrders.table.action')}</TableCell>
              </>
            )}
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {sortedHistory.map((order, index) => (
            <StyledTableRow key={`${order.time}-${index}`}>
              {currentLang === 'he' ? (
                <>
                  <TableCell align="center">
                    {(order.productType === ProductType.FILE || order.productType === ProductType.TUTORIAL) && (
                      <ActionButton
                        variant="outlined"
                        size="small"
                        startIcon={order.productType === ProductType.FILE ? <DownloadIcon /> : <OpenInNewIcon />}
                        onClick={() => handleAction(order.item || order.productDetails, order.productType, order)}
                      >
                        {order.productType === ProductType.FILE ? t('myOrders.actions.download') : t('myOrders.actions.open')}
                      </ActionButton>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <ProductImage
                        src={order.productDetails?.image || order.item?.image || order.item?.thumbnail}
                        alt={order.productDetails?.title || order.item?.title || order.item?.name}
                      >
                        <ShoppingBagIcon />
                      </ProductImage>
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      fontSize: '1rem',
                      color: '#059669',
                      letterSpacing: '0.025em'
                    }}>
                      ₪{order.amount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      lineHeight: 1.4
                    }}>
                      {order.productDetails?.title || order.item?.title || order.item?.name || t('myOrders.unknownProduct')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Chip
                      label={getProductTypeLabel(order.productType, t)}
                      size="small"
                      sx={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        border: '1px solid #e5e7eb',
                        '&:hover': {
                          backgroundColor: '#e5e7eb',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: 'text.primary',
                      mb: 0.5
                    }}>
                      {new Date(order.time).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: '0.3px'
                    }}>
                      {new Date(order.time).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                </>
              ) : (
                <>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.9rem',
                      color: 'text.primary',
                      mb: 0.5
                    }}>
                      {new Date(order.time).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      letterSpacing: '0.3px'
                    }}>
                      {new Date(order.time).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getProductTypeLabel(order.productType, t)}
                      size="small"
                      sx={{
                        backgroundColor: '#f3f4f6',
                        color: '#374151',
                        fontWeight: 500,
                        fontSize: '0.75rem',
                        border: '1px solid #e5e7eb',
                        '&:hover': {
                          backgroundColor: '#e5e7eb',
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ 
                      fontWeight: 600, 
                      fontSize: '0.95rem',
                      color: 'text.primary',
                      lineHeight: 1.4
                    }}>
                      {order.productDetails?.title || order.item?.title || order.item?.name || t('myOrders.unknownProduct')}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" sx={{ 
                      fontWeight: 600, 
                      fontSize: '1rem',
                      color: '#059669',
                      letterSpacing: '0.025em'
                    }}>
                      ₪{order.amount.toFixed(2)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      <ProductImage
                        src={order.productDetails?.image || order.item?.image || order.item?.thumbnail}
                        alt={order.productDetails?.title || order.item?.title || order.item?.name}
                      >
                        <ShoppingBagIcon />
                      </ProductImage>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    {(order.productType === ProductType.FILE || order.productType === ProductType.TUTORIAL) && (
                      <ActionButton
                        variant="outlined"
                        size="small"
                        startIcon={order.productType === ProductType.FILE ? <DownloadIcon /> : <OpenInNewIcon />}
                        onClick={() => handleAction(order.item || order.productDetails, order.productType, order)}
                      >
                        {order.productType === ProductType.FILE ? t('myOrders.actions.download') : t('myOrders.actions.open')}
                      </ActionButton>
                    )}
                  </TableCell>
                </>
              )}
            </StyledTableRow>
          ))}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );

  const renderMobileCards = () => (
    <Box>
      {sortedHistory.map((order, index) => (
        <MobileCard key={`${order.time}-${index}`}>
          <CardContent>
            <Grid container spacing={2} alignItems="center" direction={currentLang === 'he' ? 'row-reverse' : 'row'}>
              <Grid item xs={3}>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <ProductImage
                    src={order.productDetails?.image || order.item?.image || order.item?.thumbnail}
                    alt={order.productDetails?.title || order.item?.title || order.item?.name}
                  >
                    <ShoppingBagIcon />
                  </ProductImage>
                </Box>
              </Grid>
              <Grid item xs={9}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start', 
                  mb: 1,
                  flexDirection: currentLang === 'he' ? 'row-reverse' : 'row'
                }}>
                  <Typography variant="subtitle2" sx={{ 
                    fontWeight: 600, 
                    flex: 1,
                    textAlign: currentLang === 'he' ? 'right' : 'left'
                  }}>
                    {order.productDetails?.title || order.item?.title || order.item?.name || t('myOrders.unknownProduct')}
                  </Typography>
                </Box>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center', 
                  mb: 1,
                  flexDirection: currentLang === 'he' ? 'row-reverse' : 'row'
                }}>
                  <Chip
                    label={getProductTypeLabel(order.productType, t)}
                    size="small"
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                    ₪{order.amount.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ 
                  display: 'block', 
                  mb: 1,
                  textAlign: currentLang === 'he' ? 'right' : 'left'
                }}>
                  {new Date(order.time).toLocaleDateString()} {new Date(order.time).toLocaleTimeString()}
                </Typography>
                {(order.productType === ProductType.FILE || order.productType === ProductType.TUTORIAL) && (
                  <ActionButton
                    variant="outlined"
                    size="small"
                    startIcon={order.productType === ProductType.FILE ? <DownloadIcon /> : <OpenInNewIcon />}
                    onClick={() => handleAction(order.item || order.productDetails, order.productType, order)}
                    fullWidth
                  >
                    {order.productType === ProductType.FILE ? t('myOrders.actions.download') : t('myOrders.actions.open')}
                  </ActionButton>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </MobileCard>
      ))}
    </Box>
  );

  return (
    <Box sx={{ py: 6, px: { xs: 2, md: 0 } }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 600, 
          fontSize: { xs: '1.75rem', md: '2rem' },
          color: '#111827',
          letterSpacing: '-0.025em',
          mb: 1
        }}>
          {t('myOrders.title')}
        </Typography>
        <Typography variant="body1" sx={{ 
          color: '#6b7280',
          fontSize: '1rem',
          maxWidth: 500,
          mx: 'auto',
          lineHeight: 1.5
        }}>
          {t('myOrders.subtitle')}
        </Typography>
      </Box>
      
      {isMobile ? renderMobileCards() : renderDesktopTable()}
    </Box>
  );
};
